import {
  EDatasourceType,
  IActionDefinitionDto,
  IActionFolder,
  IDatabaseTableModel,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
import { ITableRowsSwitcherItem } from "../../../table-editor/table-rows-editor";
import { KuikaPickerFolder } from "../../../ui-kit/kuika-entity-picker/kuika-entity-picker";
import { KuikaEntitySwitcherItem } from "../../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import {
  DatasourceCreateCard,
  findConnector,
  getDatasourceDisplayName,
  isListableDatasource,
  isManagedDatasource
} from "../datasources-landing-model";

/**
 * What an editor's switcher chip lists: the other records of the same kind, in the order the
 * landing page shows them.
 *
 * Pure functions on purpose. Which record can be REACHED is a question about the data, and
 * it is the same question on the landing page, in the left tree and now in the bar — so it
 * is answered once here and tested here, rather than re-derived in each editor.
 */

const byName = (a: KuikaEntitySwitcherItem, b: KuikaEntitySwitcherItem): number => a.name.localeCompare(b.name);

/**
 * Managed databases are left out: no connection editor mounts for them, so picking one would
 * empty the panel. Everything else that the landing page lists is reachable.
 */
export const buildConnectionSwitcherItems = (datasources: IDatasourceDto[]): KuikaEntitySwitcherItem[] =>
  datasources
    .filter(
      (datasource: IDatasourceDto): boolean => isListableDatasource(datasource) && !isManagedDatasource(datasource)
    )
    .map((datasource: IDatasourceDto): KuikaEntitySwitcherItem => ({
      id: datasource.id,
      name: getDatasourceDisplayName(datasource)
    }))
    .sort(byName);

/** The same list, narrowed to one datasource type — what a code editor can open in place. */
export const buildDatasourceSwitcherItemsOfTypes = (
  datasources: IDatasourceDto[],
  types: EDatasourceType[]
): KuikaEntitySwitcherItem[] =>
  datasources
    .filter(
      (datasource: IDatasourceDto): boolean =>
        isListableDatasource(datasource) && types.includes(datasource.datasourceType)
    )
    .map((datasource: IDatasourceDto): KuikaEntitySwitcherItem => ({ id: datasource.id, name: datasource.name }))
    .sort(byName);

/** The actions of ONE datasource: a SQL action editor reaches its siblings, not the project's. */
export const buildActionSwitcherItems = (
  actions: IActionDefinitionDto[],
  datasourceId: string | undefined
): KuikaEntitySwitcherItem[] => {
  if (!datasourceId) return [];
  return actions
    .filter((action: IActionDefinitionDto): boolean => !!action.id && action.kDatasource_Id === datasourceId)
    .map((action: IActionDefinitionDto): KuikaEntitySwitcherItem => ({ id: action.id, name: action.name }))
    .sort(byName);
};

/** Same, narrowed to one action type — the Blockly and SAP editors open only their own. */
export const buildActionSwitcherItemsOfType = (
  actions: IActionDefinitionDto[],
  datasourceId: string | undefined,
  actionType: number
): KuikaEntitySwitcherItem[] =>
  buildActionSwitcherItems(
    actions.filter((action: IActionDefinitionDto): boolean => action.actionType === actionType),
    datasourceId
  );

/**
 * The tables of one datasource. Dropped ones are gone and a table with no id is keyed by its
 * name, the way the tree addresses it — a table's id is empty until the schema is saved.
 */
export const buildTableSwitcherItems = (tables: IDatabaseTableModel[]): KuikaEntitySwitcherItem[] =>
  tables
    .filter((table: IDatabaseTableModel): boolean => table.dropped !== true && !!table.name)
    .map((table: IDatabaseTableModel): KuikaEntitySwitcherItem => ({ id: table.id || table.name, name: table.name }))
    .sort(byName);

/**
 * The same tables, for the ROW browser: it opens a table by schema and name rather than by
 * id, and it shows the schema-qualified name, so the item carries both.
 */
export const buildRowBrowserSwitcherItems = (tables: IDatabaseTableModel[]): ITableRowsSwitcherItem[] =>
  tables
    .filter((table: IDatabaseTableModel): boolean => table.dropped !== true && !!table.name)
    .map((table: IDatabaseTableModel): ITableRowsSwitcherItem => ({
      id: `${table.schema ?? ""}.${table.name}`,
      name: table.schema ? `${table.schema}.${table.name}` : table.name,
      schema: table.schema ?? "",
      tableName: table.name
    }))
    .sort((a: ITableRowsSwitcherItem, b: ITableRowsSwitcherItem): number => a.name.localeCompare(b.name));

const toPickerFolder = (folder: IActionFolder): KuikaPickerFolder => ({
  id: folder.id,
  name: folder.name,
  itemIds: folder.actionIds ?? [],
  children: (folder.children ?? []).map(toPickerFolder)
});

/**
 * The action folders of ONE datasource, the way the left tree groups them: a folder belongs to
 * the datasource whose id it carries in `dataSourceType`, and nests one level.
 */
export const buildActionFolders = (
  folders: IActionFolder[] | undefined,
  datasourceId: string | undefined
): KuikaPickerFolder[] =>
  datasourceId
    ? (folders ?? [])
        .filter((folder: IActionFolder): boolean => folder.dataSourceType === datasourceId && !folder.parentFolderId)
        .map(toPickerFolder)
    : [];

/** The create card a connection's connector sits on — the landing's own Database / API / Function split. */
export const getConnectionGroup = (datasource: IDatasourceDto): DatasourceCreateCard =>
  findConnector(datasource.datasourceType)?.card ?? "database";
